import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import {
  useOrganization,
  type ModuleKey,
} from "../organization/OrganizationProvider";

type DashboardSummary = {
  organization_id: string;
  active_customers: number | string;
  pending_quotations: number | string;
  open_service_orders: number | string;
  low_stock_items: number | string;
  issued_accounting_documents: number | string;
};

type ExecutiveSummary = {
  open_sales: number | string;
  sales_pending_pen: number | string;
  pending_purchases: number | string;
  pending_purchases_pen: number | string;
  cash_pen: number | string;
  receivable_pen: number | string;
  payable_pen: number | string;
  overdue_receivables: number | string;
  overdue_payables: number | string;
  open_imports: number | string;
  open_reconciliations: number | string;
  reconciliation_difference_pen: number | string;
};

const modules = [
  {
    title: "Cotizaciones",
    description: "Crear propuestas, aprobarlas y convertirlas en órdenes.",
    to: "/cotizaciones",
  },
  {
    title: "Ventas",
    description: "Ventas, cobros e integración con inventario.",
    to: "/ventas",
  },
  {
    title: "Compras",
    description: "Proveedores, órdenes, recepciones e ingreso automático a inventario.",
    to: "/compras",
  },
  {
    title: "Catálogo",
    description: "Productos y servicios con precios, impuestos y control de inventario.",
    to: "/catalogo",
  },
  {
    title: "Maestros",
    description: "Proveedores y almacenes utilizados por Compras e Inventario.",
    to: "/maestros",
  },
  {
    title: "Taller",
    description: "Órdenes de servicio, estados, mano de obra, repuestos y evidencias.",
    to: "/taller",
  },
  {
    title: "Inventario",
    description: "Stock, ingresos, salidas y consumo desde órdenes de servicio.",
    to: "/inventario",
  },
  {
    title: "Importaciones",
    description: "Embarques, aduanas y seguimiento de costos de importación.",
    to: "/importaciones",
  },
  {
    title: "Clientes",
    description: "Clientes, activos e historial de interacciones.",
    to: "/clientes",
  },
  {
    title: "Activos",
    description: "Equipos, vehículos y otros bienes vinculados a clientes.",
    to: "/activos",
  },
  {
    title: "Contabilidad",
    description: "Comprobantes, periodos y operación contable.",
    to: "/contabilidad",
  },
  {
    title: "Tesorería",
    description: "Caja, bancos, cuentas por cobrar y cuentas por pagar.",
    to: "/tesoreria",
  },
  {
    title: "Conciliación",
    description: "Cruce de movimientos internos con estados bancarios.",
    to: "/conciliacion-bancaria",
  },
  {
    title: "Auditoría",
    description: "Actividad consolidada de los módulos operativos.",
    to: "/auditoria",
  },
  {
    title: "Reportes",
    description: "Balance de comprobación, resultados, posición financiera y mayor.",
    to: "/reportes",
  },
  {
    title: "Fiscal",
    description: "Control interno de periodos, RVIE/RCE, alertas y estado SUNAT/SIRE.",
    to: "/fiscal",
  },
] as const;

const ROUTE_MODULES: Array<[string, ModuleKey]> = [
  ["/cotizaciones", "quotations"],
  ["/ventas", "sales"],
  ["/compras", "purchases"],
  ["/importaciones", "purchases"],
  ["/catalogo", "catalog"],
  ["/maestros", "catalog"],
  ["/taller", "workshop"],
  ["/inventario", "inventory"],
  ["/clientes", "crm"],
  ["/activos", "crm"],
  ["/contabilidad", "accounting"],
  ["/tesoreria", "accounting"],
  ["/conciliacion-bancaria", "accounting"],
  ["/auditoria", "accounting"],
  ["/reportes", "accounting"],
  ["/fiscal", "accounting"],
];

function moduleForPath(path: string): ModuleKey | null {
  return ROUTE_MODULES.find(([prefix]) => path.startsWith(prefix))?.[1] ?? null;
}

function number(value: number | string | undefined) {
  return Number(value || 0);
}

function money(value: number | string | undefined) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(number(value));
}

export function DashboardPage() {
  const {
    selectedOrganization,
    selectedOrganizationId,
    loading: organizationLoading,
    error: organizationError,
    modulesLoading,
    isModuleEnabled,
  } = useOrganization();

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [executive, setExecutive] = useState<ExecutiveSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function loadSummary() {
      if (!selectedOrganizationId) {
        setSummary(null);
        setExecutive(null);
        setSummaryError(null);
        setSummaryLoading(false);
        return;
      }

      setSummaryLoading(true);
      setSummaryError(null);

      const [operationalResult, executiveResult] = await Promise.all([
        supabase.rpc("get_organization_dashboard_summary", {
          p_organization_id: selectedOrganizationId,
        }),
        supabase.rpc("get_organization_executive_summary", {
          p_organization_id: selectedOrganizationId,
        }),
      ]);

      if (!active) return;

      const firstError = operationalResult.error ?? executiveResult.error;

      if (firstError) {
        setSummaryError(firstError.message);
      }

      setSummary(
        operationalResult.error
          ? null
          : (((operationalResult.data ?? [])[0] as DashboardSummary | undefined) ??
              null),
      );

      setExecutive(
        executiveResult.error
          ? null
          : (executiveResult.data as ExecutiveSummary),
      );

      setSummaryLoading(false);
    }

    void loadSummary();

    return () => {
      active = false;
    };
  }, [selectedOrganizationId]);

  const metrics = useMemo(
    () => [
      {
        label: "Clientes activos",
        value: String(number(summary?.active_customers)),
        to: "/clientes",
      },
      {
        label: "Cotizaciones pendientes",
        value: String(number(summary?.pending_quotations)),
        to: "/cotizaciones",
      },
      {
        label: "Órdenes de taller abiertas",
        value: String(number(summary?.open_service_orders)),
        to: "/taller",
      },
      {
        label: "Stock bajo",
        value: String(number(summary?.low_stock_items)),
        to: "/inventario",
      },
      {
        label: "Ventas abiertas",
        value: String(number(executive?.open_sales)),
        to: "/ventas",
      },
      {
        label: "Ventas pendientes PEN",
        value: money(executive?.sales_pending_pen),
        to: "/ventas",
      },
      {
        label: "Compras pendientes",
        value: String(number(executive?.pending_purchases)),
        to: "/compras",
      },
      {
        label: "Caja y bancos PEN",
        value: money(executive?.cash_pen),
        to: "/tesoreria",
      },
      {
        label: "Por cobrar PEN",
        value: money(executive?.receivable_pen),
        to: "/tesoreria",
      },
      {
        label: "CxC vencidas",
        value: String(number(executive?.overdue_receivables)),
        to: "/tesoreria",
      },
      {
        label: "Por pagar PEN",
        value: money(executive?.payable_pen),
        to: "/tesoreria",
      },
      {
        label: "CxP vencidas",
        value: String(number(executive?.overdue_payables)),
        to: "/tesoreria",
      },
      {
        label: "Importaciones abiertas",
        value: String(number(executive?.open_imports)),
        to: "/importaciones",
      },
      {
        label: "Conciliaciones abiertas",
        value: String(number(executive?.open_reconciliations)),
        to: "/conciliacion-bancaria",
      },
      {
        label: "Diferencia bancaria PEN",
        value: money(executive?.reconciliation_difference_pen),
        to: "/conciliacion-bancaria",
      },
      {
        label: "Comprobantes emitidos",
        value: String(number(summary?.issued_accounting_documents)),
        to: "/contabilidad",
      },
    ],
    [summary, executive],
  );

  if (organizationLoading) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Cargando empresa…</strong>
          <p>Estamos preparando el contexto operativo.</p>
        </section>
      </div>
    );
  }

  if (organizationError) {
    return (
      <div className="page">
        <section className="empty-state empty-state-error">
          <strong>No se pudo cargar el contexto de empresa.</strong>
          <p>{organizationError}</p>
        </section>
      </div>
    );
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>No hay una empresa disponible para esta sesión.</strong>
          <p>La cuenta debe pertenecer a una organización activa.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Panel principal</span>
          <h1>{selectedOrganization.name}</h1>
          <p>
            Resumen operativo y financiero de la empresa seleccionada.
            {selectedOrganization.taxId
              ? ` RUC ${selectedOrganization.taxId}.`
              : ""}
          </p>
        </div>
      </header>

      {summaryError ? (
        <div className="inline-alert" role="alert">
          No se pudieron cargar todas las métricas: {summaryError}
        </div>
      ) : null}

      <section className="metric-grid" aria-label="Resumen ejecutivo">
        {metrics
          .filter((metric) => {
            const moduleKey = moduleForPath(metric.to);
            return (
              moduleKey === null ||
              modulesLoading ||
              isModuleEnabled(moduleKey)
            );
          })
          .map((metric) => (
          <Link className="metric-card" to={metric.to} key={metric.label}>
            <span>{metric.label}</span>
            <strong>{summaryLoading ? "…" : metric.value}</strong>
          </Link>
        ))}
      </section>

      <div className="section-heading">
        <div>
          <span className="eyebrow">Módulos</span>
          <h2>Operación</h2>
        </div>
      </div>

      <section className="module-grid" aria-label="Módulos">
        {modules
          .filter((module) => {
            const moduleKey = moduleForPath(module.to);
            return (
              moduleKey === null ||
              modulesLoading ||
              isModuleEnabled(moduleKey)
            );
          })
          .map((module) => (
          <Link className="module-card" to={module.to} key={module.to}>
            <div>
              <h2>{module.title}</h2>
              <p>{module.description}</p>
            </div>
            <span aria-hidden="true">→</span>
          </Link>
        ))}
      </section>
    </div>
  );
}
